/* Voice-dictation contract for Shipments (Embarques) — Fase 1 of the
   approved voice-assistant plan (2026-10-26, Arturo: "capturar un
   embarque a través de voz... me vaya preguntando... desde los
   precios, qué fruta es, etcétera").

   This is a pure data description, not UI and not save logic — it
   tells voice-turn.js (the conversational Netlify Function) which
   fields exist, their real keys (matching ShipmentRegistry.js's own
   `f.supplierId`/`f.dateDeparture`/EMPTY_LINE's `productBase`/etc. —
   never invented names), which are required, and what the valid
   options are for fields that must resolve against a real catalog
   (shipper, warehouse, product). VoiceAssistant.js reads this to
   build each request to voice-turn.js, and applies `updates` returned
   each turn straight onto a draft object with these exact keys — the
   SAME keys ShipmentForm already expects, so the draft can be handed
   to it (via setEditing(draft)) for Arturo to review/edit visually
   and save through the real, unmodified save flow.

   Fase 1 scope only: the fields Arturo explicitly named (shipper,
   dates, warehouse, product/price/cases/pallets per line). Export
   certs, brokers, transport, pallet manifest, etc. are NOT dictated —
   those stay exactly as they are today (typed/picked manually once
   Arturo opens the pre-filled form to review), same "incremental,
   don't build it all at once" principle as the rest of this app's
   roadmap. */

export function buildShipmentVoiceContract({ suppliers, warehouses }) {
  var supplierOptions = (suppliers || [])
    .filter(function (s) { return s && s.isActive !== false; })
    .map(function (s) { return { value: s.id, label: s.shortName || s.fullName || s.name || String(s.id) }; });
  var warehouseOptions = (warehouses || [])
    .filter(function (w) { return w && w.isActive !== false; })
    .map(function (w) { return { value: w.id, label: w.shortName || w.fullName || w.name || String(w.id) }; });

  return {
    module: "shipments",
    moduleLabel: "Embarque",
    fields: [
      { key: "supplierId", label: "Proveedor / Shipper", type: "select", required: true, options: supplierOptions },
      { key: "dateDeparture", label: "Fecha de salida de México (YYYY-MM-DD)", type: "date", required: true },
      { key: "dateArrival", label: "Fecha estimada de llegada (YYYY-MM-DD)", type: "date", required: true },
      { key: "warehouseId", label: "Bodega de recepción", type: "select", required: true, options: warehouseOptions },
      { key: "shipperInvoice", label: "Número de factura del proveedor", type: "text", required: false },
      { key: "entryNumber", label: "Entry number (aduana)", type: "text", required: false },
    ],
    /* One entry per product line — same shape as ShipmentRegistry.js's
       EMPTY_LINE (productBase/category/cases/pallets/price), just the
       subset that's realistic to dictate; everything else on a line
       (export cert, product description, etc.) stays for Arturo to
       fill once he reviews the pre-filled form. */
    repeatingGroup: {
      key: "lines",
      label: "Líneas de producto",
      fields: [
        { key: "productBase", label: "Producto (Blackberry, Blueberry o Raspberry)", type: "select", required: true, options: [
          { value: "Blackberry", label: "Zarzamora / Blackberry" },
          { value: "Blueberry", label: "Arándano / Blueberry" },
          { value: "Raspberry", label: "Frambuesa / Raspberry" },
        ] },
        { key: "category", label: "Categoría (Orgánico u Convencional)", type: "select", required: true, options: [
          { value: "OG", label: "Orgánico" },
          { value: "CV", label: "Convencional" },
        ] },
        { key: "cases", label: "Número de cajas", type: "number", required: true },
        { key: "pallets", label: "Número de pallets", type: "number", required: true },
        { key: "price", label: "Precio por caja (USD)", type: "number", required: true },
      ],
    },
  };
}

/* Flattens a fully-captured voice state into a draft shipment object
   with the exact field names ShipmentForm/EMPTY/EMPTY_LINE already
   use, ready to hand to setEditing(draft) in ShipmentRegistry.js — no
   new save path, the real form + real handleSave take it from here. */
export function draftShipmentFromVoiceState(state) {
  var lines = Array.isArray(state.lines) ? state.lines.map(function (l) {
    return {
      productBase: l.productBase || "",
      productName: l.productBase || "",
      category: l.category || "",
      cases: l.cases || "",
      pallets: l.pallets || "",
      price: l.price || "",
    };
  }) : [];
  return {
    supplierId: state.supplierId || "",
    dateDeparture: state.dateDeparture || "",
    dateArrival: state.dateArrival || "",
    warehouseId: state.warehouseId || "",
    shipperInvoice: state.shipperInvoice || "",
    entryNumber: state.entryNumber || "",
    lines: lines.length > 0 ? lines : undefined,
  };
}
